import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useBarsFetcher } from './useTradeStationQueries';
import {
  getRsTimeframe,
  normalizeRsTimeframeKey,
  resolveRsBarQuery,
} from '../constants/relativeStrengthTimeframes';
import {
  computeRelativeStrength,
  normalizeBarsForRelativeStrength,
  sortByRelativeStrength,
} from '../utils/relativeStrength';

/** Cap watchlist batch size so Market Overview stays responsive. */
export const RELATIVE_STRENGTH_MAX_TICKERS = 30;

/** Cold-start: reveal the table once all tickers are ready, or after this timeout. */
export const RS_COLD_REVEAL_TIMEOUT_MS = 2500;

function barsCacheKey(ticker, tf) {
  const query = resolveRsBarQuery(tf);
  if (query.firstdate && query.lastdate) {
    return `${String(ticker).toUpperCase()}|${tf.interval}|${tf.unit}|${query.firstdate}|${query.lastdate}`;
  }
  return `${String(ticker).toUpperCase()}|${tf.interval}|${tf.unit}|${tf.barsback}`;
}

function emptyLoadingRow(ticker, isLookup = false) {
  return {
    ticker,
    ok: false,
    loading: true,
    score: null,
    scorePct: null,
    windowExcessPct: null,
    tickerReturnPct: null,
    benchmarkReturnPct: null,
    barsUsed: 0,
    totalWeight: 0,
    isLookup,
  };
}

function buildRow(ticker, tickerBars, benchBars, tf, isLookup) {
  const result = computeRelativeStrength({
    tickerBars,
    benchmarkBars: benchBars,
    unit: tf.unit,
    minBars: tf.minBars,
    // YTD should use the full calendar window, not a fixed barsback cap.
    barsback: tf.range === 'ytd' ? undefined : tf.barsback,
  });
  return {
    ticker,
    ...result,
    loading: false,
    isLookup,
  };
}

/**
 * Fetch equal-sized bar windows for watchlist (+ optional lookup) vs a benchmark
 * and compute volume-weighted relative strength for the shared Market Overview timeframe.
 *
 * Bars are cached by ticker+window so adding a ticker only fetches that symbol
 * (benchmark reused). Existing rows stay visible; new rows show per-row loading
 * until their bars arrive — no full-table loading flash on incremental adds.
 */
export function useRelativeStrength({
  tickers = [],
  relativeTo = 'SPY',
  timeframeKey = 'today',
  lookupTicker = null,
  sortDirection = 'strength',
  enabled = true,
} = {}) {
  const fetchBars = useBarsFetcher();
  const fetchBarsRef = useRef(fetchBars);
  fetchBarsRef.current = fetchBars;

  const barsCacheRef = useRef(new Map());
  const benchTicker = String(relativeTo || '').trim().toUpperCase();

  const tf = useMemo(
    () => getRsTimeframe(normalizeRsTimeframeKey(timeframeKey)),
    [timeframeKey],
  );

  const symbolList = useMemo(() => {
    const seen = new Set();
    const list = [];
    const push = (raw) => {
      const sym = String(raw || '').trim().toUpperCase();
      if (!sym || seen.has(sym) || sym === benchTicker) return;
      seen.add(sym);
      list.push(sym);
    };
    (tickers || []).forEach(push);
    if (lookupTicker) push(lookupTicker);
    return list.slice(0, RELATIVE_STRENGTH_MAX_TICKERS);
  }, [tickers, lookupTicker, benchTicker]);

  const symbolKey = symbolList.join(',');
  const [loading, setLoading] = useState(false);
  const [holdDisplay, setHoldDisplay] = useState(false);
  const [error, setError] = useState(null);
  const [rows, setRows] = useState([]);
  const [lookupResult, setLookupResult] = useState(null);
  const [progress, setProgress] = useState({ done: 0, total: 0 });
  const [reloadNonce, setReloadNonce] = useState(0);

  const reload = useCallback(() => setReloadNonce((n) => n + 1), []);

  useEffect(() => {
    if (!enabled || !benchTicker) {
      setRows([]);
      setLookupResult(null);
      setLoading(false);
      setHoldDisplay(false);
      return undefined;
    }

    const symbols = symbolKey.split(',').filter(Boolean);
    const lookupSym = lookupTicker ? String(lookupTicker).trim().toUpperCase() : null;
    let cancelled = false;
    let revealTimer = null;

    const getCached = (ticker) => barsCacheRef.current.get(barsCacheKey(ticker, tf));

    const applyRows = (benchBars) => {
      const nextRows = symbols.map((ticker) => {
        const bars = getCached(ticker);
        if (!bars) return emptyLoadingRow(ticker, lookupSym === ticker);
        return buildRow(ticker, bars, benchBars, tf, lookupSym === ticker);
      });
      if (cancelled) return;
      setRows(nextRows);
      setLookupResult(lookupSym ? nextRows.find((r) => r.ticker === lookupSym) || null : null);
    };

    const releaseHold = () => {
      if (cancelled) return;
      setHoldDisplay(false);
      setLoading(false);
    };

    const missingTickers = symbols.filter((s) => !getCached(s));
    const needBench = !getCached(benchTicker);
    const toFetch = [
      ...(needBench ? [benchTicker] : []),
      ...missingTickers,
    ];

    // Warm path: everything cached — recompute instantly (e.g. sort source change / bench swap with cache).
    if (toFetch.length === 0) {
      applyRows(getCached(benchTicker) || []);
      setLoading(false);
      setHoldDisplay(false);
      setError(null);
      setProgress({ done: 1, total: 1 });
      return undefined;
    }

    // Full-table hold only on true cold start (nothing cached for this window).
    const coldStart = !getCached(benchTicker)
      && symbols.every((s) => !getCached(s));

    if (coldStart) {
      // Hide partial dashes until all ready or timeout.
      setRows([]);
      setLookupResult(null);
      setHoldDisplay(true);
      setLoading(true);
      revealTimer = setTimeout(() => {
        if (cancelled) return;
        const benchBars = getCached(benchTicker);
        if (benchBars) applyRows(benchBars);
        else {
          setRows(symbols.map((ticker) => emptyLoadingRow(ticker, lookupSym === ticker)));
        }
        setHoldDisplay(false);
      }, RS_COLD_REVEAL_TIMEOUT_MS);
    } else {
      // Incremental: show placeholders for missing symbols; keep ready rows when cached.
      setHoldDisplay(false);
      setRows((prev) => {
        const prevByTicker = new Map(prev.map((r) => [r.ticker, r]));
        const benchBars = getCached(benchTicker);
        return symbols.map((ticker) => {
          const bars = getCached(ticker);
          if (bars && benchBars) {
            return buildRow(ticker, bars, benchBars, tf, lookupSym === ticker);
          }
          if (!bars) {
            return emptyLoadingRow(ticker, lookupSym === ticker);
          }
          const existing = prevByTicker.get(ticker);
          if (existing?.loading) {
            return { ...existing, isLookup: lookupSym === ticker };
          }
          return emptyLoadingRow(ticker, lookupSym === ticker);
        });
      });
      setLoading(false);
    }

    setError(null);
    setProgress({ done: 0, total: toFetch.length });

    const bump = () => {
      if (!cancelled) {
        setProgress((prev) => ({
          done: Math.min(prev.done + 1, prev.total || toFetch.length),
          total: prev.total || toFetch.length,
        }));
      }
    };

    const fetchOne = async (ticker) => {
      const key = barsCacheKey(ticker, tf);
      if (barsCacheRef.current.has(key)) {
        bump();
        return { ticker, bars: barsCacheRef.current.get(key) };
      }
      try {
        const data = await fetchBarsRef.current({
          ticker,
          interval: tf.interval,
          unit: tf.unit,
          sessionTemplate: 'USEQPreAndPost',
          ...resolveRsBarQuery(tf),
        });
        const bars = normalizeBarsForRelativeStrength(data?.Bars || [], tf.unit);
        barsCacheRef.current.set(key, bars);
        return { ticker, bars };
      } catch (err) {
        console.error(`Relative strength bars failed for ${ticker}:`, err);
        barsCacheRef.current.set(key, []);
        return { ticker, bars: [] };
      } finally {
        bump();
      }
    };

    (async () => {
      try {
        await Promise.all(
          toFetch.map(async (ticker) => {
            await fetchOne(ticker);
            if (cancelled) return;
            const benchBars = getCached(benchTicker);
            if (!benchBars) return;
            // During cold hold, skip mid-flight UI updates — reveal when complete or timed out.
            if (coldStart && !cancelled) {
              const allReady = symbols.every((sym) => getCached(sym));
              if (allReady) {
                applyRows(benchBars);
                if (revealTimer) {
                  clearTimeout(revealTimer);
                  revealTimer = null;
                }
                releaseHold();
              }
              return;
            }
            setRows((prev) => {
              const byTicker = new Map(prev.map((r) => [r.ticker, r]));
              return symbols.map((sym) => {
                const bars = getCached(sym);
                if (bars) {
                  return buildRow(sym, bars, benchBars, tf, lookupSym === sym);
                }
                return byTicker.get(sym) || emptyLoadingRow(sym, lookupSym === sym);
              });
            });
          }),
        );
        if (cancelled) return;
        applyRows(getCached(benchTicker) || []);
        if (revealTimer) {
          clearTimeout(revealTimer);
          revealTimer = null;
        }
        releaseHold();
      } catch (err) {
        if (!cancelled) {
          console.error('Relative strength load failed:', err);
          setError(err?.message || 'Failed to load relative strength');
          releaseHold();
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
          setHoldDisplay(false);
        }
      }
    })();

    return () => {
      cancelled = true;
      if (revealTimer) clearTimeout(revealTimer);
    };
  }, [enabled, benchTicker, symbolKey, tf, lookupTicker, reloadNonce]);

  const sortedRows = useMemo(() => {
    const ready = rows.filter((r) => r.ok && !r.loading);
    const pending = rows.filter((r) => r.loading);
    const failed = rows.filter((r) => !r.ok && !r.loading);
    return [
      ...sortByRelativeStrength(ready, sortDirection),
      ...pending,
      ...failed,
    ];
  }, [rows, sortDirection]);

  const failedRows = useMemo(
    () => rows.filter((r) => !r.ok && !r.loading),
    [rows],
  );

  return {
    loading: loading || holdDisplay,
    error,
    progress,
    rows: holdDisplay ? [] : sortedRows,
    failedRows,
    lookupResult: holdDisplay ? null : lookupResult,
    timeframe: tf,
    reload,
  };
}

export default useRelativeStrength;
